import React from 'react'
import { useQuery } from '@apollo/client'
import {
  TestSessions,
  TestSessionsVariables,
  SortDirectionEnum,
  TestSessionStatus,
  Lab,
  TestSessionPaymentStatus,
} from '../../types'
import { SortByType } from '../../constants'
import { TEST_SESSIONS } from './operations'

interface TestSessionsFilter {
  statuses?: TestSessionStatus[]
  dateFrom?: string
  dateTo?: string
  userId?: string
  groupId?: string
  groupIds?: string[]
  companyIds?: string[]
  companyId?: string
  companyOrGroup?: string
  userNameOrEmail?: string
  isParticipant?: boolean
  isStaff?: boolean
  lab?: Lab
  userIds?: string[]
  paymentStatus?: TestSessionPaymentStatus
  invoicedFrom?: string
  invoicedTo?: string
}

export type Sorter = {
  sortBy?: string
  direction?: SortDirectionEnum
}

interface Props {
  filter?: TestSessionsFilter
  sorter?: Sorter
  page?: number
  pageLength?: number
  skip?: boolean
}

const PAGE_LENGTH = 10

export const useTestSessionsData = ({
  filter,
  sorter,
  page = 1,
  pageLength = PAGE_LENGTH,
  skip = false,
}: Props) => {
  const {
    userId,
    companyIds,
    companyId,
    groupId,
    groupIds,
    companyOrGroup,
    userNameOrEmail,
    dateFrom,
    dateTo,
    statuses,
    isParticipant,
    isStaff,
    lab,
    userIds,
    paymentStatus,
    invoicedFrom,
    invoicedTo,
  } = filter || {}
  const queryVariables = React.useMemo<TestSessionsVariables>(
    () => ({
      filterData: {
        dateBetween: [dateFrom, dateTo],
        invoicedBetween: [invoicedFrom, invoicedTo],
        statuses,
        paymentStatus,
        companyIds,
        groupIds,
        lab,
        company:
          companyOrGroup || companyId
            ? {
                nameOrGroupNameLike: companyOrGroup,
                id: companyId,
              }
            : null,
        group: groupId ? { id: groupId } : null,
        user:
          userNameOrEmail || userId || isParticipant !== undefined || isStaff !== undefined
            ? {
                id: userId,
                emailOrNameLike: userNameOrEmail,
                isParticipant,
                isStaff,
              }
            : null,
      },
      groupsInputData: {
        groupIds,
        memberUserIds: userIds,
      },
      companyInputData: {
        memberUserIds: userIds,
      },
      pagination: {
        from: pageLength * (page - 1),
        length: pageLength,
        sortBy: sorter?.sortBy || SortByType.DATE,
        direction: sorter?.direction || SortDirectionEnum.DESC,
      },
    }),
    [
      dateFrom,
      dateTo,
      statuses,
      paymentStatus,
      companyIds,
      groupIds,
      companyOrGroup,
      companyId,
      groupId,
      userNameOrEmail,
      userId,
      isParticipant,
      isStaff,
      page,
      pageLength,
      sorter?.sortBy,
      sorter?.direction,
      lab,
      userIds,
      invoicedFrom,
      invoicedTo,
    ]
  )
  const testSessionsQuery = useQuery<TestSessions, TestSessionsVariables>(TEST_SESSIONS, {
    variables: queryVariables,
    skip,
  })

  return testSessionsQuery
}
